import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { organizationSaveErrorMessage } from "@/hooks/useOrganizations";
import { countryOptionsFor } from "@/components/corporate/organizations/orgOptions";
import { SaveBar, SectionFrame } from "@/components/corporate/organizations/page/SectionFrame";
import { useReportDirty, useSectionDraft } from "@/components/corporate/organizations/page/useSectionDraft";
import { invalidateOrganization, putOrganization, textOrNull } from "@/components/corporate/organizations/page/orgSave";
import type { Organization } from "@/types/organizations";
import type { UpdateOrganizationRequest } from "@/types/generated/cms.generated";

interface CompanyDetailsSectionProps {
  organization: Organization;
  onDirtyChange?: (dirty: boolean) => void;
}

interface CompanyDetails {
  name: string;
  taxRegistrationNumber: string;
  address: string;
  postalCode: string;
  city: string;
  state: string;
  country: string;
}

type DetailField = keyof CompanyDetails;

const FIELDS: DetailField[] = ["name", "taxRegistrationNumber", "address", "postalCode", "city", "state", "country"];

const fromOrganization = (o: Organization): CompanyDetails => ({
  name: o.name ?? "",
  taxRegistrationNumber: o.taxRegistrationNumber ?? "",
  address: o.address ?? "",
  postalCode: o.postalCode ?? "",
  city: o.city ?? "",
  state: o.state ?? "",
  country: o.country ?? "",
});

export const COMPANY_NAME_REQUIRED = "Company name is required";

/**
 * The body of the save: only the fields that changed (compared trimmed). A cleared field is sent
 * as `null`, except the tax number, which is cleared with `""` (`null` means "unchanged" for it).
 */
export function companyDetailsBody(values: CompanyDetails, base: CompanyDetails): UpdateOrganizationRequest {
  const body: UpdateOrganizationRequest = {};
  for (const field of FIELDS) {
    const next = values[field].trim();
    if (next === base[field].trim()) continue;
    if (field === "taxRegistrationNumber") body.taxRegistrationNumber = next;
    else if (field === "name") body.name = next;
    else body[field] = textOrNull(next);
  }
  return body;
}

/** Settings → Company details: name, tax number, address and country, saved on their own (OP-D14). */
export function CompanyDetailsSection({ organization, onDirtyChange }: CompanyDetailsSectionProps) {
  const queryClient = useQueryClient();
  const draft = useSectionDraft(fromOrganization(organization));
  const { values, set } = draft;
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const changed = Object.keys(companyDetailsBody(values, draft.base)).length > 0;
  // The leave guard uses the same trimmed comparison as Save: a space alone is not an edit.
  useReportDirty(onDirtyChange, changed);

  const onSave = async () => {
    if (saving) return;
    if (!values.name.trim()) {
      setNameError(COMPANY_NAME_REQUIRED);
      toast({ title: "Validation Error", description: COMPANY_NAME_REQUIRED, variant: "destructive" });
      return;
    }
    const body = companyDetailsBody(values, draft.base);
    if (Object.keys(body).length === 0) {
      draft.markSaved(values);
      return;
    }
    setSaving(true);
    try {
      await putOrganization(organization.id, body);
      draft.markSaved(values);
      invalidateOrganization(queryClient, organization.id);
      toast({ title: "Company details saved", description: "Only the fields you changed were saved." });
    } catch (error) {
      toast({
        title: "Error",
        description: organizationSaveErrorMessage(error, "Couldn't save the company details. Please try again."),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const text = (field: DetailField, label: string, props: { maxLength?: number; placeholder?: string } = {}) => {
    const id = `org-details-${field}`;
    const error = field === "name" ? nameError : null;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          value={values[field]}
          onChange={(e) => {
            set(field, e.target.value);
            if (field === "name" && e.target.value.trim()) setNameError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          {...props}
        />
        {error && (
          <p id={`${id}-error`} className="text-sm text-ds-err">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <SectionFrame
      section="details"
      title="Company details"
      hint="The company's registered name, tax number and address."
      footer={
        <SaveBar
          dirty={changed}
          saving={saving}
          onSave={onSave}
          onCancel={() => {
            draft.discard();
            setNameError(null);
          }}
          note="Saves only the fields you changed."
        />
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        {text("name", "Company name *", { maxLength: 255 })}
        {text("taxRegistrationNumber", "Tax registration number", { maxLength: 50 })}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="org-details-address">Address</Label>
        <Textarea
          id="org-details-address"
          rows={2}
          value={values.address}
          onChange={(e) => set("address", e.target.value)}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {text("city", "City")}
        {text("state", "State / emirate")}
        {text("postalCode", "Postal code")}
        <div className="space-y-1.5">
          <Label htmlFor="org-details-country">Country</Label>
          <Select value={values.country} onValueChange={(value) => set("country", value)}>
            <SelectTrigger id="org-details-country">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {countryOptionsFor(draft.base.country).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </SectionFrame>
  );
}

export default CompanyDetailsSection;
